import {
  BarChart3,
  BookOpen,
  Banknote,
  Building2,
  CalendarDays,
  Clock,
  FileSignature,
  FolderKanban,
  Handshake,
  Home,
  KeyRound,
  PackageCheck,
  Radar, // Phase M (m-ui)
  KeySquare,
  Landmark,
  LifeBuoy,
  Receipt,
  ReceiptText,
  Settings,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { Permission, PlanModule } from '@denwen-msp/shared';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  permission?: Permission;
  /** Only for staff users linked to an HR employee record. */
  needsEmployee?: boolean;
  /** Phase I (I-B): hidden when the tenant's plan leaves this module out. */
  planModule?: PlanModule;
  /** Phase I (I-D): only for platform admins (Denwen MSP's own staff). */
  platformOnly?: boolean;
}

/** Main sidebar. Items the user can't access are hidden; the API enforces it anyway. */
export const NAV: { section?: string; items: NavItem[] }[] = [
  {
    items: [
      { label: 'Home', to: '/', icon: Home },
      { label: 'My Space', to: '/me', icon: UserRound, needsEmployee: true, planModule: 'hr' },
    ],
  },
  {
    section: 'Service',
    items: [
      { label: 'Service desk', to: '/tickets', icon: LifeBuoy, permission: 'tickets.read' },
      { label: 'Schedule', to: '/schedule', icon: CalendarDays, permission: 'schedule.read' },
      { label: 'Time', to: '/time', icon: Clock, permission: 'time.read' },
      { label: 'Monitoring', to: '/monitoring', icon: Radar, permission: 'monitoring.read', planModule: 'monitoring' }, // Phase M (m-ui): Denwen MSP Monitor
    ],
  },
  {
    section: 'Clients',
    items: [
      { label: 'Organizations', to: '/organizations', icon: Building2, permission: 'organizations.read' },
      { label: 'Contacts', to: '/contacts', icon: Users, permission: 'organizations.read' },
      { label: 'Passwords', to: '/passwords', icon: KeyRound, permission: 'passwords.read' }, // B1 (vault track)
      { label: 'Sales', to: '/crm', icon: Handshake, permission: 'crm.read' }, // Phase D CRM track (D8); Track 4: crm.*
      { label: 'Knowledge base', to: '/knowledge', icon: BookOpen, permission: 'docs.read' },
      { label: 'Ask the docs', to: '/ask', icon: Sparkles, permission: 'docs.read' }, // Phase F (F5): AI documentation Q&A
    ],
  },
  {
    section: 'Delivery',
    items: [
      { label: 'Projects', to: '/projects', icon: FolderKanban, permission: 'projects.read' },
      { label: 'Contracts', to: '/agreements', icon: FileSignature, permission: 'agreements.read' },
      { label: 'Billing', to: '/billing', icon: Receipt, permission: 'billing.read' },
      { label: 'Licences', to: '/licences', icon: KeySquare, permission: 'billing.read' }, // Phase D CRM track (D10)
      { label: 'Purchasing', to: '/purchasing', icon: PackageCheck, permission: 'purchasing.read' }, // Phase D CRM track (D11); Track 4
      { label: 'Accounting', to: '/accounting', icon: Landmark, permission: 'accounting.read', planModule: 'accounting' }, // G1 (ledger core)
      { label: 'Bills', to: '/payables', icon: ReceiptText, permission: 'accounting.read', planModule: 'accounting' }, // G2 (payables)
      { label: 'Banking', to: '/banking', icon: Banknote, permission: 'accounting.read', planModule: 'accounting' }, // G3/G4 (g-banking)
    ],
  },
  {
    section: 'People',
    items: [
      { label: 'HR', to: '/hr', icon: UserRound, permission: 'hr.read', planModule: 'hr' },
      { label: 'Payroll', to: '/payroll', icon: Banknote, permission: 'payroll.read', planModule: 'payroll' }, // Phase H: UK payroll
    ],
  },
  {
    section: 'Insights',
    items: [{ label: 'Reports', to: '/reports', icon: BarChart3, permission: 'reports.read' }],
  },
  {
    section: 'Admin',
    items: [
      { label: 'Automation', to: '/automation', icon: Workflow, permission: 'automation.read' },
      { label: 'Settings', to: '/admin', icon: Settings, permission: 'admin.read' },
      { label: 'Platform', to: '/platform', icon: ShieldCheck, platformOnly: true }, // Phase I (I-D): the platform console
    ],
  },
];
